import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { FiDownload, FiFilter, FiRefreshCw } from 'react-icons/fi';
import Button from '../../Shared/Button';
import { notify } from '../../Shared/toast';

function InventoryDashboard() {
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [language, setLanguage] = useState('en');

  // Translations
  const translations = {
    en: {
      title: 'Inventory Dashboard',
      totalItems: 'Total Items',
      totalQuantity: 'Total Quantity',
      lowStock: 'Low Stock Alerts',
      recentUpdates: 'Recent Inventory Updates',
      item: 'Item',
      quantity: 'Quantity',
      lastUpdated: 'Last Updated',
      kg: 'kg',
      download: 'Download Report',
      refresh: 'Refresh',
      filters: 'Filters',
      all: 'All',
      low: 'Low Stock',
      critical: 'Critical',
    },
    bn: {
      title: 'ইনভেন্টরি ড্যাশবোর্ড',
      totalItems: 'মোট আইটেম',
      totalQuantity: 'মোট পরিমাণ',
      lowStock: 'কম স্টক সতর্কতা',
      recentUpdates: 'সাম্প্রতিক ইনভেন্টরি আপডেট',
      item: 'আইটেম',
      quantity: 'পরিমাণ',
      lastUpdated: 'সর্বশেষ আপডেট',
      kg: 'কেজি',
      download: 'রিপোর্ট ডাউনলোড',
      refresh: 'রিফ্রেশ',
      filters: 'ফিল্টার',
      all: 'সব',
      low: 'কম স্টক',
      critical: 'সমালোচনামূলক',
    },
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        // Replace setTimeout with actual API call
        await new Promise((resolve) => {
          setTimeout(resolve, 800);
        });

        if (!isMounted) return;

        const mockData = [
          {
            id: 1, item: language === 'en' ? 'Rice' : 'চাল', quantity: 100, lastUpdated: '2025-06-25', status: 'normal',
          },
          {
            id: 2, item: language === 'en' ? 'Oil' : 'তেল', quantity: 15, lastUpdated: '2025-06-26', status: 'low',
          },
          {
            id: 3, item: language === 'en' ? 'Sugar' : 'চিনি', quantity: 200, lastUpdated: '2025-06-27', status: 'normal',
          },
          {
            id: 4, item: language === 'en' ? 'Lentils' : 'ডাল', quantity: 5, lastUpdated: '2025-06-28', status: 'critical',
          },
          {
            id: 5, item: language === 'en' ? 'Flour' : 'ময়দা', quantity: 80, lastUpdated: '2025-06-29', status: 'normal',
          },
        ];

        setInventoryData(mockData);
      } catch (error) {
        notify.error(language === 'en' ? 'Failed to load data' : 'ডেটা লোড করতে ব্যর্থ হয়েছে');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [language]);

  const getFilteredData = () => {
    if (filter === 'all') return inventoryData;
    if (filter === 'low') return inventoryData.filter((item) => item.status === 'low');
    return inventoryData.filter((item) => item.status === 'critical');
  };

  const filteredData = getFilteredData();

  const getStatusText = (status) => {
    if (status === 'normal') return language === 'en' ? 'Normal' : 'স্বাভাবিক';
    if (status === 'low') return language === 'en' ? 'Low' : 'কম';
    return language === 'en' ? 'Critical' : 'সমালোচনামূলক';
  };

  const getStatusClass = (status) => {
    if (status === 'normal') return 'bg-green-100 text-green-800';
    if (status === 'low') return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const totalItems = inventoryData.length;
  const totalQuantity = inventoryData.reduce((sum, item) => sum + item.quantity, 0);
  const lowStockItems = inventoryData.filter((item) => item.status !== 'normal').length;

  const handleDownload = () => {
    notify.success(language === 'en' ? 'Report download started' : 'রিপোর্ট ডাউনলোড শুরু হয়েছে');
  };

  const handleRefresh = () => {
    notify.info(language === 'en' ? 'Refreshing data...' : 'ডেটা রিফ্রেশ করা হচ্ছে...');
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'bn' : 'en'));
  };

  return (
    <div className="p-4 md:p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            {translations[language].title}
          </h2>
          <Button
            onClick={toggleLanguage}
            size="sm"
            variant="outline"
          >
            {language === 'en' ? 'বাংলা' : 'English'}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleDownload}
            size="md"
            variant="outline"
          >
            <FiDownload className="inline mr-2" />
            {translations[language].download}
          </Button>
          <Button
            onClick={handleRefresh}
            size="md"
            disabled={loading}
          >
            {loading ? (
              'Loading...'
            ) : (
              <>
                <FiRefreshCw className="inline mr-2" />
                {translations[language].refresh}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Inventory Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-4 shadow rounded-xl border border-gray-200">
          <p className="text-gray-500 text-sm">{translations[language].totalItems}</p>
          <p className="text-2xl font-bold text-green-600">{totalItems}</p>
        </div>
        <div className="bg-white p-4 shadow rounded-xl border border-gray-200">
          <p className="text-gray-500 text-sm">{translations[language].totalQuantity}</p>
          <p className="text-2xl font-bold text-blue-600">
            {totalQuantity}
            {' '}
            {translations[language].kg}
          </p>
        </div>
        <div className="bg-white p-4 shadow rounded-xl border border-gray-200">
          <p className="text-gray-500 text-sm">{translations[language].lowStock}</p>
          <p className="text-2xl font-bold text-yellow-600">{lowStockItems}</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-4 shadow rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            {language === 'en' ? 'Inventory Levels' : 'ইনভেন্টরি পর্যায়'}
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={filteredData}>
                <XAxis dataKey="item" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="quantity"
                  fill="#4f46e5"
                  name={translations[language].quantity}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-4 shadow rounded-xl border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            {language === 'en' ? 'Stock Status' : 'স্টক অবস্থা'}
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: language === 'en' ? 'Normal' : 'স্বাভাবিক', value: inventoryData.filter((i) => i.status === 'normal').length, fill: '#10b981' },
                { name: language === 'en' ? 'Low' : 'কম', value: inventoryData.filter((i) => i.status === 'low').length, fill: '#f59e0b' },
                { name: language === 'en' ? 'Critical' : 'সমালোচনামূলক', value: inventoryData.filter((i) => i.status === 'critical').length, fill: '#ef4444' },
              ]}
              >
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white shadow rounded-xl overflow-auto border border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border-b gap-4">
          <h3 className="text-xl font-semibold text-gray-800">
            {translations[language].recentUpdates}
          </h3>

          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border p-2 rounded text-sm"
            >
              <option value="all">{translations[language].all}</option>
              <option value="low">{translations[language].low}</option>
              <option value="critical">{translations[language].critical}</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3">{translations[language].item}</th>
                  <th className="text-left px-4 py-3">{translations[language].quantity}</th>
                  <th className="text-left px-4 py-3">{translations[language].lastUpdated}</th>
                  <th className="text-left px-4 py-3">{language === 'en' ? 'Status' : 'অবস্থা'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-gray-50 transition"
                  >
                    <td className="px-4 py-3 font-medium">{item.item}</td>
                    <td className="px-4 py-3">
                      {item.quantity}
                      {' '}
                      {translations[language].kg}
                    </td>
                    <td className="px-4 py-3">{item.lastUpdated}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusClass(item.status)}`}>
                        {getStatusText(item.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                {language === 'en' ? 'No inventory items found' : 'কোনো ইনভেন্টরি আইটেম পাওয়া যায়নি'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default InventoryDashboard;
